import _ from 'lodash';
import { useState, useEffect, forwardRef } from 'react';
import {
  makeStyles,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  RadioGroup,
  TextField,
  FormLabel,
  Button,
  Typography,
  MenuItem,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { StringUtils } from '../../../../../common/utils';
import { selectDiscountReasons } from '../../../state/subscriptionSelectors';
import { useSelector } from 'react-redux';
import { LoadingWrapper } from '../../../../../common/components';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  switch: {
    margin: '-4px 0 0 0',
    alignItems: 'flex-start',
    '& .MuiSwitch-root': {
      marginLeft: '-8px',
    },
    display: 'none',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
}));

type DiscountForm = {
  reason: number | '';
  duration: number | '';
  type: string;
  value: number | '';
}

interface SubscriptionDiscountFormProps {
  discount?: {
    reason?: number;
    type?: string;
    duration?: number;
    value?: number;
  };
  cancelButtonText?: string;
  cancelButtonIcon?: string;
  saveButtonText?: string;
  saveButtonIcon?: string;
  onSave: (data: DiscountForm) => Promise<void>;
  onCancel: () => void;
  actionsDisabled?: boolean;
  onFetchDiscountReasons: () => void;
}

export const SubscriptionDiscountForm = forwardRef<unknown, SubscriptionDiscountFormProps>((props, ref) => {
  const {
    onFetchDiscountReasons,
    onSave,
    onCancel,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
  } = props;
  const reasons = useSelector(selectDiscountReasons);

  const [t] = useTranslation();

  const classes = useStyles();

  const durationOptions = [30, 60, 90];
  const percentValueOptions = [10, 25, 50];

  const loading = !reasons;
  const [discountType, setDiscountType] = useState('PERCENT');
  const formatDiscountValueSuffix = () => {
    return discountType === 'PERCENT' ?
     '%' :
     'Kr.';
  };

  const getLabel = (label: string, required = true) => {
    return `${t(label)}${required ? ' *' : ''}`;
  };

  const { control, errors, formState, handleSubmit } =
    useForm<DiscountForm>({
      defaultValues: {
        reason: '',
        type: discountType,
        duration: '',
        value: '',
      },
  });

  const { isDirty, isSubmitting } = formState;

  const editable = isSubmitting || !actionsDisabled;

  const handleCancel = () => {
    onCancel();
  };

  const onSubmit = async (data: DiscountForm) => {
    try {
      await onSave(data);
    } catch (e) {
      // Error already handled
    }
  };

  useEffect(() => {
    if (!reasons) {
      onFetchDiscountReasons();
    }
  }, [reasons, onFetchDiscountReasons]);

  return (
    <Box id="DiscountForm-container" className={classes.container}>
      <LoadingWrapper loading={loading}>
      <form id="DiscountForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Alert severity="info">
                {t('subscription.subscriptionDiscountInfo')}
              </Alert>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="reason"
                control={control}
                rules={{
                  required: t('subscription.subscriptionDiscountReasonRequired') as string,
                }}
                error={!!errors.reason}
                helperText={errors.reason?.message}
                as={
                  <TextField
                    id="DiscountForm-discountReason"
                    label={getLabel('subscription.subscriptionDiscountReason')}
                    variant="outlined"
                    size="small"
                    disabled={!editable}
                    fullWidth
                    select
                  >
                    {(reasons ?? []).map((option: { reasonCode: number; displayText: string }) => (
                      <MenuItem key={option.reasonCode} value={option.reasonCode}>
                        { option.displayText }
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="duration"
                control={control}
                rules={{
                  required: t('subscription.subscriptionDiscountDurationRequired') as string,
                }}
                error={!!errors.duration}
                helperText={errors.duration?.message}
                as={
                  <TextField
                    id="DiscountForm-discountDuration"
                    label={getLabel('subscription.subscriptionDiscountDuration')}
                    variant="outlined"
                    size="small"
                    disabled={!editable}
                    fullWidth
                    select
                  >
                    {durationOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        { StringUtils.getFormattedDays(option) }
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
            <Grid item xs={6} className={classes.switch}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{getLabel('subscription.subscriptionDiscountType')}</FormLabel>
                <Controller
                    name="type"
                    control={control}
                    defaultValue={discountType}
                    rules={{
                      required: t('subscription.subscriptionDiscountTypeRequired') as string,
                    }}
                    error={!!errors.type}
                    helperText={errors.type?.message}
                    render={(params) => (
                      <RadioGroup id="DiscountForm-discountType"
                        value={params.value}
                        onChange={e => { setDiscountType(e.target.value); params.onChange(e); }}
                      >
                        <FormControlLabel
                            value="PERCENT"
                            control={<Radio />}
                            label={t('subscription.subscriptionDiscountTypePercent')}
                            disabled={!editable}
                        />
                        <FormControlLabel
                            value="AMOUNT"
                            control={<Radio />}
                            label={t('subscription.subscriptionDiscountTypeAmount')}
                            disabled
                        />
                      </RadioGroup>
                    )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <Controller
                name="value"
                control={control}
                rules={{
                  required: t('subscription.subscriptionDiscountValueRequired') as string,
                }}
                error={!!errors.value}
                helperText={errors.value?.message}
                as={
                  <TextField
                    id="DiscountForm-discountValuePercent"
                    label={getLabel('subscription.subscriptionDiscountTypePercent')}
                    variant="outlined"
                    size="small"
                    disabled={!editable}
                    fullWidth
                    select
                  >
                    {percentValueOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option} {formatDiscountValueSuffix()}
                      </MenuItem>
                    ))}
                  </TextField>
                }
              />
            </Grid>
          </Grid>
        </Box>
        <Box id='DiscountForm-actions' className={classes.actions}>
          <Button
            id="DiscountForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? cancelButtonIcon : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? cancelButtonText : t('common.cancel')}
            </Typography>
          </Button>
          <Button
            id="DiscountForm-save"
            key="save"
            type="submit"
            color="primary"
            className={classes.button}
            startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'create')}</Icon>}
            disabled={actionsDisabled || isSubmitting || !isDirty}
          >
            <Typography variant="button" noWrap>
              {_.defaultTo(saveButtonText, t('common.create'))}
            </Typography>
          </Button>
        </Box>
      </form>
      </LoadingWrapper>
    </Box>
  );
});

export default SubscriptionDiscountForm;
